import { reactive } from 'vue';

/**
 * Tiny imperative dialog system for app-wide confirm/prompt modals.
 *
 * Usage anywhere in the app:
 *   import { useDialog } from '@/composables/useDialog';
 *   const dialog = useDialog();
 *   if (await dialog.confirm({ title: 'Delete?', message: '…', danger: true })) { … }
 *   const name = await dialog.prompt({ title: 'New page', label: 'Page name' });
 *
 * `<DialogHost />` must be mounted once near the root of the app to render
 * the actual modal UI.
 */

const state = reactive({
  current: null // { kind, opts, resolve }
});

function open(kind, opts) {
  return new Promise((resolve) => {
    state.current = { kind, opts, resolve };
  });
}

function resolveAndClose(value) {
  const cur = state.current;
  if (!cur) return;
  state.current = null;
  cur.resolve(value);
}

export function useDialog() {
  return {
    confirm({
      title,
      message,
      confirmLabel = 'Confirm',
      cancelLabel = 'Cancel',
      danger = false
    }) {
      return open('confirm', { title, message, confirmLabel, cancelLabel, danger });
    },
    prompt({
      title,
      message = '',
      label = '',
      placeholder = '',
      defaultValue = '',
      confirmLabel = 'Save',
      cancelLabel = 'Cancel',
      validate = null,
      // Optional secondary input — the modal shows it below the main input,
      // auto-syncs from `withSlug.fromValue(mainValue)` while the user types
      // until they edit it manually. The resolve value becomes
      // `{ value, slug }` instead of a bare string.
      withSlug = null
    }) {
      return open('prompt', {
        title, message, label, placeholder, defaultValue, confirmLabel, cancelLabel, validate, withSlug
      });
    }
  };
}

// Internal API used by <DialogHost />
export function _useDialogState() {
  return state;
}

export function _resolveDialog(value) {
  resolveAndClose(value);
}
